import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, signal } from '@angular/core';
import {
    AvatarComponent,
    ButtonDirective,
    CardBodyComponent,
    CardComponent,
    CardGroupComponent,
    ColComponent,
    ContainerComponent,
    DropdownItemDirective,
    RowComponent,
    SpinnerComponent,
    TextColorDirective,
} from '@coreui/angular';
import { IconDirective } from '@coreui/icons-angular';
import { AuthService } from '@auth0/auth0-angular';
import { AsyncPipe, NgClass, NgForOf, NgTemplateOutlet } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { LoadingComponent } from '../../partials/loading/loading.component';
import { SessionService } from '../../../services/session/session.service';
import { catchError, combineLatest, filter, map, of, switchMap, tap } from 'rxjs';
import { UsersApiService } from '../../../services/session/users-api.service';
import { ButtonModule } from 'primeng/button';
import { Organization, Role } from '../../../services/session/user.model';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TooltipModule } from 'primeng/tooltip';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';

@Component({
    standalone: true,
    imports: [
        ContainerComponent,
        RowComponent,
        ColComponent,
        CardGroupComponent,
        CardComponent,
        CardBodyComponent,
        ButtonDirective,
        AsyncPipe,
        AvatarComponent,
        TextColorDirective,
        RouterLink,
        LoadingComponent,
        DropdownItemDirective,
        NgForOf,
        ButtonModule,
        NgClass,
        SpinnerComponent,
        IconDirective,
        TooltipModule,
        NgTemplateOutlet,
    ],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
    readonly roleInfo: Partial<Record<Role, { title: string; subtitle: string }>> = {
        ROOT_ADMIN: { title: 'Root Admin', subtitle: 'Full system access across all accounts' },
        ACCOUNT_ADMIN: { title: 'Account Admin', subtitle: 'Manage organizations within an account' },
        ORG_ADMIN: { title: 'Org Admin', subtitle: 'Manage data, users, and settings within an organization' },
        ORG_MEMBER: { title: 'Org Member', subtitle: 'Read-only access to organization data' },
    };

    notAccessToAccount = signal(JSON.parse(this.localStorageService.get('notAccessToAccount', 'false')));
    authUser = toSignal(this.auth.user$);
    originalRole = toSignal(this.session.originalRole$.pipe(tap(d => {
        if (!d) this.notAccessToAccount.set(JSON.parse(this.localStorageService.get('notAccessToAccount', 'false')));
        else this.localStorageService.remove('notAccessToAccount');
    })));
    assumedRole = toSignal(this.session.assumedRole$);
    roleLabel = computed(() => this.userApi.roleToLabel(this.assumedRole() || ''));

    userContext = toSignal(this.session.userContext$);

    isRoleLoading = computed(() => {
        const role = this.originalRole();
        if (!role) return true;
        if (role === 'ROOT_ADMIN') return false;
        return this.userContext() === undefined;
    });

    // Roles available to this user derived from context
    availableRoles = computed((): Role[] => {
        if (this.originalRole() === 'ROOT_ADMIN') return ['ORG_ADMIN', 'ORG_MEMBER', 'ACCOUNT_ADMIN', 'ROOT_ADMIN'];
        const ctx = this.userContext();
        if (!ctx) return [];
        const roles: Role[] = [];
        if (ctx.orgs.some(o => o.user_role === 'ORG_ADMIN')) roles.push('ORG_ADMIN');
        if (ctx.orgs.some(o => o.user_role === 'ORG_MEMBER')) roles.push('ORG_MEMBER');
        if (ctx.accounts.length > 0) roles.push('ACCOUNT_ADMIN');
        return roles;
    });

    // Step 1: Role picker shown when user has multiple role types or is ROOT_ADMIN
    isRolePickerNeeded = toSignal(this.session.isRolePickerNeeded$, { initialValue: false });
    rolePickerDone = signal(false);
    isRoleSelected = computed(() => !this.isRolePickerNeeded() || this.rolePickerDone());

    // Step 2: Account options
    selectedAccount = signal<{ id: string; name: string } | null>(null);
    expandedAccounts = signal<Map<string, Organization[] | null>>(new Map());
    orgErrors = signal<Set<string>>(new Set());
    accountOptionsError = signal(false);

    // ROOT_ADMIN: fetch all accounts from API when role is selected.
    // Combine both streams so the filter re-evaluates whenever EITHER role resolves
    // (fixes race where originalRole$ hadn't emitted at the time assumedRole$ fired).
    private _rootAdminAccounts = toSignal(
        combineLatest([this.session.assumedRole$, this.session.originalRole$]).pipe(
            filter(([role, orig]) => !!role && orig === 'ROOT_ADMIN'),
            switchMap(() => this.userApi.getAccounts$().pipe(
                map(accounts => accounts.map(a => ({ id: a.account_id, name: a.name }))),
                catchError(() => { this.accountOptionsError.set(true); return of<{ id: string; name: string }[]>([]); })
            ))
        )
    );

    // Derived synchronously from signals — no injection context issues
    accountOptions = computed((): { id: string; name: string }[] | undefined => {
        const role = this.assumedRole();
        if (!role) return undefined;

        if (this.originalRole() === 'ROOT_ADMIN') {
            return this._rootAdminAccounts(); // undefined while loading, then array
        }

        const ctx = this.userContext();
        if (ctx === undefined) return undefined; // still loading
        if (!ctx) return []; // error (null context)

        if (role === 'ACCOUNT_ADMIN') {
            return ctx.accounts.map(a => ({ id: a.account_id, name: a.account_name }));
        }
        // ORG roles: derive unique accounts from orgs
        const m = new Map<string, { id: string; name: string }>();
        ctx.orgs.forEach(o => m.set(o.account_id, { id: o.account_id, name: o.account_name }));
        return Array.from(m.values());
    });

    // Step 3: Org selection needed for org-level roles and ROOT_ADMIN
    needsOrgStep = computed(() => {
        const role = this.assumedRole();
        return role === 'ORG_ADMIN' || role === 'ORG_MEMBER' || role === 'ROOT_ADMIN';
    });

    // When org role + all orgs belong to a single account: skip account list, show orgs directly.
    // account_id on the user is informational only — does not need to match the org's account.
    skippedAccountOrgs = computed((): Organization[] | null => {
        const ctx = this.userContext();
        const role = this.assumedRole();
        if (!ctx) return null;
        if (role !== 'ORG_ADMIN' && role !== 'ORG_MEMBER') return null;
        const uniqueAccounts = new Set(ctx.orgs.map(o => o.account_id));
        if (uniqueAccounts.size !== 1) return null;
        return ctx.orgs.map(o => ({
            org_id: o.org_id,
            name: o.org_name,
            account_id: o.account_id,
            account_name: o.account_name,
        }));
    });

    constructor(
        private auth: AuthService,
        private session: SessionService,
        private userApi: UsersApiService,
        private localStorageService: LocalStorageService,
        private router: Router,
        private destroyRef: DestroyRef
    ) {
        const navState = this.router.getCurrentNavigation()?.extras.state;
        if (navState?.['skipRolePicker']) {
            this.rolePickerDone.set(true);
        }

        const preselectedAccount = navState?.['preselectedAccount'] as { id: string; name: string } | undefined;

        // Auto-navigate or auto-select when role is resolved and single option
        effect(() => {
            if (!this.isRoleSelected() || this.selectedAccount() || this.expandedAccounts().size > 0) return;

            if (preselectedAccount) {
                this.selectAccount(preselectedAccount);
                return;
            }

            const role = this.assumedRole();
            const ctx = this.userContext();

            // Single org, all in one account → go directly to that org (no account list needed)
            if (ctx && (role === 'ORG_ADMIN' || role === 'ORG_MEMBER') && ctx.orgs.length === 1) {
                const uniqueAccounts = new Set(ctx.orgs.map(o => o.account_id));
                if (uniqueAccounts.size === 1) {
                    const org = ctx.orgs[0];
                    this.useOrganization({
                        org_id: org.org_id,
                        name: org.org_name,
                        account_id: org.account_id,
                        account_name: org.account_name,
                    });
                    return;
                }
            }

            const accounts = this.accountOptions();
            if (accounts?.length === 1) {
                this.selectAccount(accounts[0]);
            }
        }, { allowSignalWrites: true });
    }

    assumeRole(role: Role) {
        this.session.roleOverride$.next(role);
        this.rolePickerDone.set(true);
    }

    clearRoleSelection() {
        this.rolePickerDone.set(false);
        this.selectedAccount.set(null);
        this.expandedAccounts.set(new Map());
        this.orgErrors.set(new Set());
        this.session.roleOverride$.next('');
    }

    selectAccount(account: { id: string; name: string }) {
        const role = this.assumedRole()!;

        if (role === 'ACCOUNT_ADMIN' || role === 'SYSTEM_ADMIN') {
            this.selectedAccount.set(account);
            this.session.setAccountOrg(account)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.router.navigate(['/accounts', account.id, 'organizations']));
            return;
        }

        // Org-level roles: toggle account in multi-expand accordion
        const expanded = new Map(this.expandedAccounts());
        if (expanded.has(account.id)) {
            expanded.delete(account.id);
            const errors = new Set(this.orgErrors());
            errors.delete(account.id);
            this.orgErrors.set(errors);
            this.expandedAccounts.set(expanded);
            return;
        }

        const ctx = this.userContext();
        if (this.originalRole() !== 'ROOT_ADMIN' && ctx) {
            // Derive orgs from context — no API call needed
            const orgs: Organization[] = ctx.orgs
                .filter(o => o.account_id === account.id)
                .map(o => ({
                    org_id: o.org_id,
                    name: o.org_name,
                    account_id: o.account_id,
                    account_name: o.account_name,
                }));
            expanded.set(account.id, orgs);
            this.expandedAccounts.set(expanded);
        } else {
            // ROOT_ADMIN: fetch orgs for this account from API
            expanded.set(account.id, null);
            this.expandedAccounts.set(expanded);

            this.userApi.getAccountOrganizations$(account.id).pipe(
                catchError(() => {
                    const errors = new Set(this.orgErrors());
                    errors.add(account.id);
                    this.orgErrors.set(errors);
                    return of<Organization[]>([]);
                }),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(orgs => {
                const map = new Map(this.expandedAccounts());
                map.set(account.id, orgs);
                this.expandedAccounts.set(map);
            });
        }
    }

    clearAccountSelection() {
        this.selectedAccount.set(null);
        this.expandedAccounts.set(new Map());
        this.orgErrors.set(new Set());
    }

    useOrganization(org: Organization) {
        this.session
            .setAccountOrg({ id: org.account_id, name: org.account_name }, org)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.router.navigate(['/orgs', org.org_id, 'dashboard']));
    }

    logout() {
        this.localStorageService.remove('notAccessToAccount');
        this.session.clearAccountOrg();
        this.auth.logout({ logoutParams: { returnTo: document.location.origin + '/login' } });
    }

    login() {
        this.session.clearAccountOrg();
        this.auth.loginWithRedirect();
    }
}
